import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tag, Search, Save, X, Euro, Loader2, CheckCircle2, Package, Pencil, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { upsertCustomerPrice, buildPriceHistory, lastPurchase } from '@/lib/customerPriceApi';
import { History } from 'lucide-react';

const fmtDate = (d) => new Date(d).toLocaleDateString('de-DE');

/**
 * CustomerPriceManager
 * Inline per-row price editing — one price per (customer, product) pair.
 */
export default function CustomerPriceManager({ leadId, leadName }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null); // product.id currently being edited
  const [inputValue, setInputValue] = useState('');   // raw string for the expanded input
  const queryClient = useQueryClient();

  /* ── Data ─────────────────────────────────────────────── */
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: customerPrices = [], isLoading: pricesLoading } = useQuery({
    queryKey: ['customer-prices', leadId],
    queryFn: () => base44.entities.CustomerPrice.filter({ lead_id: leadId }),
    enabled: !!leadId,
  });

  // Müşterinin geçmiş siparişleri → tarih damgalı fiyat geçmişi.
  // Zam sonrası "eskiden kaça alıyordu?" sorusunun cevabı buradan gelir.
  const { data: leadOrders = [] } = useQuery({
    queryKey: ['lead-orders', leadId],
    queryFn: () => base44.entities.Order.filter({ lead_id: leadId }),
    enabled: !!leadId,
  });

  const historyMap = useMemo(() => buildPriceHistory(leadOrders), [leadOrders]);

  /* ── Price map: product_id → CustomerPrice record ──────── */
  const priceMap = useMemo(() => {
    const m = {};
    customerPrices.forEach(cp => { m[cp.product_id] = cp; });
    return m;
  }, [customerPrices]);

  /* ── Mutations ─────────────────────────────────────────── */
  const saveMutation = useMutation({
    mutationFn: async ({ productId, price }) => {
      const product = products.find(p => p.id === productId);
      // Ortak upsert: taze DB sorgusu + preis/custom_price senkron yazımı
      return upsertCustomerPrice({
        leadId,
        leadName: leadName || '',
        productId,
        productName: product?.name || '',
        productSku: product?.sku || '',
        price,
        originalPrice: product?.price ?? 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['customer-prices', leadId]);
      toast.success('Kundenpreis gespeichert ✓');
      setExpandedId(null);
      setInputValue('');
    },
    onError: (err) => {
      toast.error('Fehler: ' + err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (priceId) => base44.entities.CustomerPrice.delete(priceId),
    onSuccess: () => {
      queryClient.invalidateQueries(['customer-prices', leadId]);
      toast.success('Kundenpreis entfernt');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  /* ── Handlers ──────────────────────────────────────────── */
  const openEdit = (product) => {
    const existing = priceMap[product.id];
    setExpandedId(product.id);
    setInputValue(
      existing?.custom_price != null
        ? String(existing.custom_price)
        : product.price != null ? String(product.price) : ''
    );
  };

  const closeEdit = () => {
    setExpandedId(null);
    setInputValue('');
  };

  const handleSave = (productId) => {
    const parsed = parseFloat(inputValue);
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Bitte einen gültigen Preis eingeben');
      return;
    }
    saveMutation.mutate({ productId, price: parsed });
  };

  /* ── Filter + akıllı sıralama ──────────────────────────── */
  // Daha önce sipariş verilen / özel fiyatı olan ürünler EN ÜSTTE
  // (son satın alma tarihine göre yeniden eskiye), kalanlar altta.
  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    const base = !searchTerm ? products : products.filter(p =>
      p.name?.toLowerCase().includes(t) ||
      p.sku?.toLowerCase().includes(t)
    );
    const rank = (p) => {
      const last = lastPurchase(historyMap, p.id);
      if (last) return new Date(last.date).getTime();      // en yeni alım en üstte
      if (priceMap[p.id]) return 1;                        // fiyatı var ama alım yok
      return 0;                                            // hiç ilişki yok
    };
    return [...base].sort((a, b) => rank(b) - rank(a) || (a.name || '').localeCompare(b.name || ''));
  }, [products, searchTerm, historyMap, priceMap]);

  const isLoading = productsLoading || pricesLoading;

  if (!leadId) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-slate-400">
        <Package className="w-10 h-10 mb-2 opacity-40" />
        <p className="text-sm">Kein Kunde ausgewählt</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-blue-600" />
          <span className="font-semibold text-slate-800">Kundenpreise</span>
        </div>
        {customerPrices.length > 0 && (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            {customerPrices.length} Sonderpreis{customerPrices.length > 1 ? 'e' : ''} aktiv
          </Badge>
        )}
      </div>

      {/* ── Search ── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Produkt suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* ── Product list with inline editing ── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden divide-y">
          {filtered.map(product => {
            const cp = priceMap[product.id];
            const hasCP = !!cp;
            const isOpen = expandedId === product.id;
            const stdPrice = product.price;
            const last = lastPurchase(historyMap, product.id);
            const history = historyMap.get(product.id) || [];

            return (
              <div key={product.id} className={cn(
                'transition-colors',
                hasCP ? 'bg-blue-50/40' : 'bg-white',
                isOpen && 'bg-blue-50'
              )}>
                {/* ── Row summary ── */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 active:bg-slate-100"
                  onClick={() => isOpen ? closeEdit() : openEdit(product)}
                >
                  {/* Product image / icon */}
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border flex-shrink-0 overflow-hidden">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-4 h-4 text-slate-300 m-auto mt-2.5" />
                    )}
                  </div>

                  {/* Name + SKU */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-sm text-slate-800 truncate">{product.name}</p>
                      {hasCP && <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-400">{product.sku}</p>
                    {last && (
                      <p className="text-[11px] text-emerald-700 flex items-center gap-1 mt-0.5">
                        <History className="w-3 h-3 flex-shrink-0" />
                        Zuletzt €{last.price.toFixed(2)} am {fmtDate(last.date)}
                        {history.length > 1 && <span className="text-slate-400">· {history.length}× gekauft</span>}
                      </p>
                    )}
                  </div>

                  {/* Price display */}
                  <div className="text-right flex-shrink-0">
                    {hasCP ? (
                      <>
                        <p className="text-sm font-bold text-blue-600">€{cp.custom_price?.toFixed(2)}</p>
                        <p className="text-xs text-slate-400 line-through">€{stdPrice?.toFixed(2)}</p>
                      </>
                    ) : (
                      <p className="text-sm font-medium text-slate-600">€{stdPrice?.toFixed(2)}</p>
                    )}
                  </div>

                  {/* Edit icon */}
                  <Pencil className={cn(
                    'w-4 h-4 flex-shrink-0 transition-colors',
                    isOpen ? 'text-blue-500' : 'text-slate-300'
                  )} />
                </div>

                {/* ── Inline edit panel ── */}
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 bg-blue-50 border-t border-blue-100">
                    <p className="text-xs text-slate-500 mb-2">
                      Kundenpreis für <span className="font-medium text-slate-700">{product.name}</span> festlegen:
                    </p>
                    <div className="flex items-center gap-2">
                      {/* Price input */}
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">€</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onFocus={(e) => e.target.select()}
                          placeholder="0.00"
                          autoFocus
                          className="w-full h-11 pl-7 pr-3 text-base font-semibold border-2 border-blue-300 rounded-lg bg-white focus:outline-none focus:border-blue-500"
                        />
                      </div>

                      {/* Save */}
                      <Button
                        className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                        onClick={() => handleSave(product.id)}
                        disabled={saveMutation.isPending}
                      >
                        {saveMutation.isPending
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <><Save className="w-4 h-4 mr-1.5" />Speichern</>
                        }
                      </Button>

                      {/* Delete (only if price exists) */}
                      {hasCP && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Kundenpreis entfernen"
                          className="h-11 w-11 text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                          onClick={() => deleteMutation.mutate(cp.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}

                      {/* Close */}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Bearbeitung schließen"
                        className="h-11 w-11 text-slate-400 hover:bg-slate-100 flex-shrink-0"
                        onClick={closeEdit}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Hint */}
                    <p className="text-xs text-slate-400 mt-1.5">
                      Standardpreis: €{stdPrice?.toFixed(2)}
                      {hasCP && (
                        <span className="ml-2 text-blue-500">
                          · Aktuell: €{cp.custom_price?.toFixed(2)}
                        </span>
                      )}
                    </p>

                    {/* Tarih damgalı fiyat geçmişi — zam sonrası referans */}
                    {history.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-blue-100">
                        <p className="text-[10px] font-bold uppercase text-slate-500 flex items-center gap-1 mb-1.5">
                          <History className="w-3 h-3" /> Preisverlauf (Bestellungen)
                        </p>
                        <div className="space-y-1 max-h-36 overflow-y-auto">
                          {history.slice(0, 8).map((h, i) => (
                            <button
                              key={i}
                              type="button"
                              className="w-full flex items-center justify-between text-[11px] bg-white border border-slate-200 rounded-md px-2.5 py-1.5 hover:border-blue-300 cursor-pointer transition-colors"
                              title="Preis übernehmen"
                              onClick={() => setInputValue(String(h.price))}
                            >
                              <span className="text-slate-500">
                                {fmtDate(h.date)}{h.orderNo ? ` · ${h.orderNo}` : ''}{h.quantity ? ` · ${h.quantity} Stk` : ''}
                              </span>
                              <span className={cn(
                                'font-mono font-bold',
                                i === 0 ? 'text-emerald-700' : 'text-slate-700'
                              )}>
                                €{h.price.toFixed(2)}
                              </span>
                            </button>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">Tipp: Zeile antippen übernimmt den Preis ins Eingabefeld.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="py-8 text-center text-slate-400 text-sm">
              Keine Produkte gefunden
            </div>
          )}
        </div>
      )}
    </div>
  );
}
